import { useSnackbar } from 'notistack';
import { useEffect, useState } from "react";
// @mui
import {
  Box,
  Button,
  IconButton,
  Step,
  StepLabel,
  StepContent,
  Stepper,
  Typography,
  Stack,
  Card,
  CardContent,
  Link,
} from "@mui/material";
// hooks
import useAuth from '../../../hooks/useAuth';
import useResponsive from '../../../hooks/useResponsive';
// components
import Iconify from "../../../components/Iconify";
import SvgIconStyle from "../../../components/SvgIconStyle";
import {
  fKrKSTFile,
  fKrDateDayKST,
  fKrTimeKST,
} from "../../../utils/formatTime";


// ----------------------------------------------------------------------

export default function ProgressPanel({
  coffeeChat,
  isOpenOfferMessage, openOfferMessage, closeOfferMessage,
  openMeetingConfirmDialog,
  isOpenCheckMeetingSchedule, openCheckMeetingSchedule, closeCheckMeetingSchedule,
  moveToContinueTab,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState(0);
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');

  const getStep = () => {
    if (!coffeeChat) return 0;
    if (coffeeChat.coffee_chat_status === "offer-to-crew") return 0;
    if (coffeeChat.coffee_chat_status === "offer-to-company") return 1;
    if (coffeeChat.coffee_chat_status === "other-time-requested") return 1;
    if (coffeeChat.coffee_chat_status === "crew-decline") return 1;
    if (coffeeChat.coffee_chat_status === "meeting-selected") return 2;
    if (coffeeChat.coffee_chat_status === "meeting-confirmed") return 2;
    if (coffeeChat.coffee_chat_status === "meeting-done") return 3;
    return 0;
  };

  const getSecondStepLabelText = () => {
    if (!coffeeChat) return "";
    if (coffeeChat.coffee_chat_status === "offer-to-crew") return "크루의 커피챗 수락/거절";
    if (coffeeChat.coffee_chat_status === "crew-decline") return "크루의 커피챗 거절";
    return "크루의 커피챗 수락";
  };

  const getFourthStepLabelText = () => {
    if (!coffeeChat) return "";
    return "커피챗 완료";
  };

  const getSecondStepContent = () => {
    if (!coffeeChat) return "";
    if (coffeeChat.coffee_chat_status === "offer-to-crew") return "크루가 커피챗을 수락하면, 크루가 커피챗 가능한 일정을 보내게 됩니다.";
    if (coffeeChat.coffee_chat_status === "offer-to-company") return "크루가 커피챗 가능 일정을 보냈어요. 커피챗 일정을 확정해 주세요.";
    if (coffeeChat.coffee_chat_status === "other-time-requested") return "크루에게 다른 커피챗 가능 일정을 요청했어요. 크루가 가능한 일정을 다시 보내면 알려드릴게요.";
    if (coffeeChat.coffee_chat_status === "crew-decline") return "크루가 커피챗을 거절했어요. 크루에게 특별히 전달하고 싶은 내용이 있다면 메시지를 보내보세요.";
    return "크루가 커피챗 가능 일정을 보냈어요. 커피챗 일정을 확정해 주세요.";
  };

  const getThirdStepContent = () => {
    if (!coffeeChat) return "";
    if (coffeeChat.coffee_chat_status === "meeting-selected") return "커피챗 일정과 화상 미팅 링크를 확인해주세요.";
    if (coffeeChat.coffee_chat_status === "meeting-confirmed") return "커피챗 일정과 화상 미팅 링크를 확인해주세요.";
    if (coffeeChat.coffee_chat_status === "meeting-done") return "커피챗 일정과 화상 미팅 링크를 확인해주세요.";
    return "회사가 커피챗 일정을 확정하고, 화상 미팅으로 커피챗을 진행해요.";
  };

  const getFourthStepContent = () => {
    if (!coffeeChat) return "";
    if (coffeeChat.coffee_chat_status === "meeting-done") return "커피챗은 잘 마치셨나요? 크루와 네트워크를 이어가보세요.";
    return "커피챗 후 크루와 네트워크를 이어가보세요.";
  };

  useEffect(() => {
    setStep(getStep());
  }, [coffeeChat]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(coffeeChat?.meeting_location_content);
      enqueueSnackbar('Zoom 링크를 복사하였습니다', { variant: 'info' });
    } catch (err) {
      console.error('failed to copy: ', err);
    }
  };

  if (!coffeeChat) return <></>;
  if (coffeeChat?.coffee_chat_status === "wait") return <></>;

  if (isOpenOfferMessage) {
    return (
      <>
        {/* < 버튼 표시 */}
        <Box sx={{ display: "flex", mb: 2 }}>
          <IconButton color="inherit" edge="start" onClick={closeOfferMessage}>
            <SvgIconStyle src={`/icons/chevron-left.svg`} sx={{ width: 32, height: 32, color: "text.secondary" }} />
          </IconButton>
        </Box>
        <Typography variant="h5" paragraph>
          크루에게 커피챗 제안을 보냈어요.
        </Typography>
        <Stack direction="row" alignItems={"start"} spacing={1}>
          <Typography variant="subtitle2">
            보낸사람
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={.5}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {coffeeChat?.coffee_chat_by_dio ? "DIO 매니저" : user?.username}
            </Typography>
            {smUp && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              •
            </Typography>
            )}
            <Typography variant="body2" sx={{ minWidth: 175, color: "text.secondary" }} noWrap>
              {fKrKSTFile(coffeeChat?.coffee_chat_request_at)} 발송
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" alignItems={"start"} spacing={1} sx={{ mt: 1 }}>
          <Typography variant="subtitle2">
            받는사람
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={.5}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {coffeeChat?.crew?.nickname} 크루
            </Typography>
            {smUp && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              •
            </Typography>
            )}
            {!coffeeChat?.first_seen_at && (
              <Typography variant="body2" sx={{ color: "text.disabled" }}>
                확인하지 않음
              </Typography>
            )}
            {coffeeChat?.first_seen_at && (
            <Typography variant="body2" sx={{ minWidth: 175, color: "text.secondary" }} noWrap>
              {fKrKSTFile(coffeeChat?.first_seen_at)} 확인
            </Typography>
            )}
          </Stack>
        </Stack>
        <Typography variant="subtitle1" sx={{ mt: 3 }} gutterBottom>
          메시지
        </Typography>
        <Box
          sx={{
            p: 2.5,
            backgroundColor: "grey.200",
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Typography variant="body1" whiteSpace={"pre-wrap"} sx={{ wordBreak: "break-all" }}>
            {coffeeChat.message}
          </Typography>
        </Box>
        {coffeeChat?.offer_salary_range && (
          <>
          <Typography variant="subtitle1" gutterBottom>
            제안한 조건
          </Typography>
          <Box
            sx={{
              p: 2.5,
              backgroundColor: "grey.200",
              borderRadius: 2,
            }}
          >
            <Stack spacing={2}>
              <Stack spacing={.5}>
                <Stack direction="row" alignItems={"center"} spacing={1}>
                  <SvgIconStyle src={`/icons/icon-user-mono.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="subtitle1">
                    포지션
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ pl: 3 }}>
                  {coffeeChat.offer_position}
                </Typography>
              </Stack>
              <Stack spacing={.5}>
                <Stack direction="row" alignItems={"center"} spacing={1}>
                  <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="subtitle1">
                    연봉
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ pl: 3 }}>
                  {coffeeChat.offer_salary_range}
                </Typography>
              </Stack>
              <Stack spacing={.5}>
                <Stack direction="row" alignItems={"center"} spacing={1}>
                  <Iconify icon="mdi:user-box" width={16} height={16} sx={{ color: "text.disabled" }} />
                  <Typography variant="subtitle1">
                    직급
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ pl: 3 }}>
                  {coffeeChat.offer_title}
                </Typography>
              </Stack>
              {coffeeChat.offer_stock_option && (
              <Stack spacing={.5}>
                <Stack direction="row" alignItems={"center"} spacing={1}>
                  <SvgIconStyle src={`/icons/icon-percent-circle-mono.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="subtitle1">
                    스톡옵션 조건
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ pl: 3 }}>
                  {coffeeChat.offer_stock_option}
                </Typography>
              </Stack>
              )}
              {coffeeChat.offer_remote_work && coffeeChat.offer_remote_work !== "재택근무 없음(전면 상주근무)" && (
              <Stack spacing={.5}>
                <Stack direction="row" alignItems={"center"} spacing={1}>
                  <SvgIconStyle src={`/icons/icon-building-mono.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="subtitle1">
                    재택근무 여부
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ pl: 3 }}>
                  {coffeeChat.offer_remote_work}
                </Typography>
              </Stack>
              )}
            </Stack>
          </Box>
          </>
        )}
      </>
    )
  }

  if (isOpenCheckMeetingSchedule) {
    return (
      <>
        {/* < 버튼 표시 */}
        <Box sx={{ display: "flex", mb: 1 }}>
          <IconButton color="inherit" edge="start" onClick={closeCheckMeetingSchedule}>
            <SvgIconStyle src={`/icons/chevron-left.svg`} sx={{ width: 32, height: 32, color: "text.secondary" }} />
          </IconButton>
        </Box>

        <Stack>
          <Typography variant="h5" gutterBottom>
            커피챗 일정과, <br/>
            화상 미팅 링크를 확인해주세요.
          </Typography>
          <Typography>
            안내해드린 일정에 아래 Zoom 링크로 미팅에 참여해주세요.
          </Typography>
          <Card sx={{ mt: 4 }}>
            <CardContent>
            <Stack spacing={2}>
              {/* date */}
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ color: "secondary.main" }} />
                  <Typography variant="subtitle1">
                    커피챗 일자
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ pl: 4 }}>
                  {fKrDateDayKST(coffeeChat?.meeting_start_time)}
                </Typography>
              </Stack>

              {/* time */}
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SvgIconStyle src={`/icons/icon-clock-mono.svg`} sx={{ color: "secondary.main" }} />
                  <Typography variant="subtitle1">
                    커피챗 시간
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ pl: 4 }}>
                  {fKrTimeKST(coffeeChat?.meeting_start_time)}
                </Typography>
              </Stack>

              {/* zoom link */}
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Iconify icon="eva:video-fill" width={24} height={24} sx={{ color: "secondary.main" }} />
                  <Typography variant="subtitle1">
                    Zoom 링크
                  </Typography>
                </Stack>
                <Stack sx={{ pl: 4, alignItems: "flex-start" }} spacing={0.5}>
                  <Link variant="subtitle1" underline="always" sx={{ wordBreak: "break-all", textDecorationColor: (theme) => theme.palette.primary.main }} color="text.primary" href={coffeeChat?.meeting_location_content} target="_blank" rel="noopener">
                    {coffeeChat?.meeting_location_content}
                  </Link>
                </Stack>
                <Box sx={{ pl: 4 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ borderColor: (theme) => theme.palette.grey[500_32] }}
                    onClick={copyLink}
                    startIcon={<SvgIconStyle src={`/icons/icon-share-mono.svg`} sx={{ width: 18, height: 18, color: "#8B95A1"}} />}
                  >
                    링크 복사
                  </Button>
                </Box>
              </Stack>

            </Stack>
            </CardContent>
          </Card>
        </Stack>
      </>
    )
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        커피챗 진행 단계
      </Typography>
      <Stepper activeStep={step} orientation="vertical">
        {/* step 0 (화면에선 1) */}
        <Step
          expanded
          sx={{
            '& .MuiSvgIcon-root': {
              color: (theme) => `${theme.palette.info.main} !important`
            },
          }}
        >
          <StepLabel
            sx={{
              "& .MuiStepLabel-label": {
                color: step === 0 ? "text.primary" : "text.secondary",
                fontSize: (theme) => theme.typography.subtitle1.fontSize,
                fontWeight: (theme) => theme.typography.subtitle1.fontWeight,
                lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
              }
            }}
          >
            커피챗 제안 보냄
          </StepLabel>
          <StepContent>
            {!coffeeChat?.first_seen_at && step === 0 && (
              <Typography variant="body2" paragraph sx={{ color: "text.disabled" }}>
                크루가 제안을 확인하지 않음
              </Typography>
            )}
            {!coffeeChat?.first_seen_at && step > 0 && (
            <Typography variant="body2" paragraph
              sx={{ color: step === 0 ? "text.primary" : "text.disabled" }}
            >
              크루에게 커피챗 제안을 보냈어요. 크루가 수락 또는 거절하면 알려드릴게요.
            </Typography>
            )}
            {coffeeChat?.first_seen_at && (
              <Stack direction="row" spacing={1} sx={{ pb: 2 }}>
                <Typography variant="body2"
                  sx={{ color: step === 0 ? "text.primary" : "text.disabled" }}
                >
                  크루가 제안을 확인함
                </Typography>
                <Typography variant="body2" sx={{ color: "text.disabled" }}>
                  {fKrKSTFile(coffeeChat?.first_seen_at)}
                </Typography>
              </Stack>
            )}
            {step === 0 && coffeeChat?.coffee_chat_status === "offer-to-crew" && (
            <Box>
              <Button
                variant="outlined"
                color="info"
                size="large"
                onClick={openOfferMessage}
              >
                보낸 제안 확인하기
              </Button>
            </Box>
            )}
          </StepContent>
        </Step>
        {/* step 1 (화면에선 2) */}
        <Step
          expanded
          sx={{
            '& .MuiSvgIcon-root': {
              color: (theme) => step < 1 ? `${theme.palette.text.disabled} !important` : `${theme.palette.info.main} !important`
            },
          }}
        >
          <StepLabel
            sx={{
              "& .MuiStepLabel-label": {
                fontSize: (theme) => theme.typography.subtitle1.fontSize,
                fontWeight: (theme) => theme.typography.subtitle1.fontWeight,
                lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
                color: step === 1 ? "text.primary" : step > 1 ? "text.secondary" : "text.disabled"
              }
            }}
          >
            {getSecondStepLabelText()}
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph
              sx={{ color: step === 1 ? "text.primary" : "text.disabled" }}
            >
              {getSecondStepContent()}
            </Typography>
            {step === 1 && coffeeChat?.coffee_chat_status === "offer-to-company" && (
            <Box>
              <Button
                variant="contained"
                size="large"
                onClick={openMeetingConfirmDialog}
              >
                커피챗 일정 확정하기
              </Button>
            </Box>
            )}
          </StepContent>
        </Step>
        {/* step 2 (화면에선 3) */}
        <Step
          expanded
          sx={{
            '& .MuiSvgIcon-root': {
              color: (theme) => step < 2 ? `${theme.palette.text.disabled} !important` : `${theme.palette.info.main} !important`
            },
          }}
        >
          <StepLabel
            sx={{
              "& .MuiStepLabel-label": {
                fontSize: (theme) => theme.typography.subtitle1.fontSize,
                fontWeight: (theme) => theme.typography.subtitle1.fontWeight,
                lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
                color: step === 2 ? "text.primary" : step > 2 ? "text.secondary" : "text.disabled"
              }
            }}
          >
            커피챗 일정 확정
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph
              sx={{ color: step === 2 ? "text.primary" : "text.disabled" }}
            >
              {getThirdStepContent()}
            </Typography>
            {step === 2 && (coffeeChat?.coffee_chat_status === "meeting-confirmed" || coffeeChat?.coffee_chat_status === "meeting-selected") && (
            <Box>
              <Button
                variant="outlined"
                color="info"
                size="large"
                onClick={openCheckMeetingSchedule}
              >
                커피챗 일정 확인하기
              </Button>
            </Box>
            )}
          </StepContent>
        </Step>
        {/* step 3 (화면에선 4) */}
        <Step
          expanded
          sx={{
            '& .MuiSvgIcon-root': {
              color: (theme) => step < 3 ? `${theme.palette.text.disabled} !important` : `${theme.palette.info.main} !important`
            },
          }}
        >
          <StepLabel
            sx={{
              "& .MuiStepLabel-label": {
                fontSize: (theme) => theme.typography.subtitle1.fontSize,
                fontWeight: (theme) => theme.typography.subtitle1.fontWeight,
                lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
                color: step === 3 ? "text.primary" : "text.disabled"
              }
            }}
          >
            {getFourthStepLabelText()}
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph
              sx={{ color: step === 3 ? "text.primary" : "text.disabled" }}
            >
              {getFourthStepContent()}
            </Typography>
            {step === 3 && coffeeChat?.coffee_chat_status === "meeting-done" && (
            <Box>
              <Button
                variant="contained"
                size="large"
                onClick={moveToContinueTab}
              >
                크루와 이어가기
              </Button>
            </Box>
            )}
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  )
}
